import * as d3 from 'd3';

export default class Chart {
  constructor(
    el,
    data,
    {
      veryPositiveColour = 'green',
      positiveColour = 'blue',
      negativeColour = 'yellow',
      neutralColour = '#eee',
      veryNegativeColour = 'red',
      highlightColour = 'pink',
      selectedColour = 'green',
    } = {},
  ) {
    this.el = el;
    this.data = data.map((el, idx) => {
      el.chartID = idx;
      return el;
    });
    this.veryPositiveColour = veryPositiveColour;
    this.positiveColour = positiveColour;
    this.negativeColour = negativeColour;
    this.neutralColour = neutralColour;
    this.veryNegativeColour = veryNegativeColour;
    this.highlightColour = highlightColour;
    this.selectedColour = selectedColour;
    const self = this;

    this.selected = {
      selected: [],
      indicators: new Map(),
      add(d) {
        this.selected.push(d);
        d3.select(this.indicators.get(d)).attr('fill', selectedColour);
        self.emitChange();
      },
      remove(d) {
        this.selected.splice(this.selected.indexOf(d), 1);
        d3.select(this.indicators.get(d)).attr('fill', 'transparent');
        self.emitChange();
      },
      includes(d) {
        return this.selected.includes(d);
      },
      addIndicator(d, el) {
        this.indicators.set(d, el);
      },
    };
    this.drag = null;
    this.changeHandlers = [];
    this.selected = new SelectionHandler(this.changeHandlers, selectedColour);

    // Handle setting minimum width for a bar
    this.minWidth = data.length * 25;
    this.boundingWidth = el.getBoundingClientRect().width;

    this.fullWidth = this.minWidth < this.boundingWidth ? this.boundingWidth : this.minWidth;

    this.height = 400;
    this.width = this.boundingWidth;

    if (this.fullWidth > this.width) {
      this.addZoom();
    }
  }

  async createChart() {
    this.chart = d3
      .select(this.el)
      .attr('width', this.width)
      .attr('height', this.height)
      .attr('viewBox', '0 0 ' + this.width + ' ' + this.height)
      .append('g');

    const chart = this.chart;
    const labelBarHeight = 50;
    const positiveHeight = (this.height - labelBarHeight) / 2;

    /**
     * Scales
     */
    const x = d3
      .scaleBand()
      .domain(this.data.map((el) => el.chartID))
      .range([0, this.width])
      .paddingOuter(0.5)
      .align(0.5);

    const y = d3.scaleLinear().domain([0, 1]).range([0, positiveHeight]);

    /**
     * Labels setup
     */
    const labelBar = chart
      .append('g')
      .attr('transform', 'translate(0, ' + (this.height / 2 - labelBarHeight / 2) + ')');

    const selected = this.selected;
    const bgRects = labelBar
      .selectAll('rect')
      .data(this.data)
      .enter()
      .append('rect')
      .attr('fill', 'white')
      .attr('stroke', 'black')
      .attr('height', 50)
      .attr('width', x.bandwidth())
      .attr('x', (d) => x(d.chartID))
      .each(function (d) {
        selected.addIndicator(d, this);
      });

    const highlightColour = this.highlightColour;
    const labels = labelBar
      .selectAll('g')
      .data(this.data)
      .enter()
      .append('g')
      .attr('width', x.bandwidth())
      .attr('height', '200')
      .attr('transform', (d) => 'translate(' + x(d.chartID) + ', 0)')
      .on('mouseover', function (d) {
        d3.select(this).selectAll('.flyout').style('visibility', 'visible');

        if (!selected.includes(d)) {
          const self = this;
          if (!this.bgdRect) {
            bgRects.each(function (bgD) {
              if (d == bgD) {
                self.bgdRect = this;
              }
            });
          }

          d3.select(this.bgdRect).attr('fill', highlightColour);
        }
      })
      .on('mouseout', function (d) {
        d3.select(this).selectAll('.flyout').style('visibility', 'hidden');

        if (!selected.includes(d)) {
          const self = this;
          if (!this.bgdRect) {
            bgRects.each(function (bgD) {
              if (d == bgD) {
                self.bgdRect = this;
              }
            });
          }

          d3.select(this.bgdRect).attr('fill', 'white');
        }
      })
      .style('cursor', 'pointer')
      .on('click', function (d) {
        const self = this;
        if (!this.bgdRect) {
          bgRects.each(function (bgD) {
            if (d == bgD) {
              self.bgdRect = this;
            }
          });
        }

        if (!selected.includes(d)) {
          selected.add(d);
        } else {
          selected.remove(d);
        }
      });

    // Required to trigger mouse events on <g> elements above
    const labelRects = labels
      .append('rect')
      .attr('width', x.bandwidth())
      .attr('height', '200')
      .attr('fill', 'transparent');

    this.labelRects = labelRects;
    const thirdMarker = this.data.length / 3;

    labels
      .append('text')
      .attr('y', labelBarHeight / 2)
      .attr('x', (d, i) => {
        if (i < thirdMarker) {
          return 10;
        } else if (i >= thirdMarker && i < thirdMarker * 2) {
          return x.bandwidth() / 2;
        } else {
          return x.bandwidth() - 10;
        }
      })
      .attr('text-anchor', (d, i) => {
        if (i < thirdMarker) {
          return 'start';
        } else if (i >= thirdMarker && i < thirdMarker * 2) {
          return 'middle';
        } else {
          return 'end';
        }
      })
      .attr('dominant-baseline', 'central')
      .text((d) => d.response)
      .style('visibility', 'hidden')
      .attr('class', 'flyout');

    labels
      .insert('rect', ':first-child')
      .attr('width', function () {
        return d3.select(this.parentNode).select('text').node().getBBox().width;
      })
      .attr('height', labelBarHeight * 0.6)
      .attr('rx', 5)
      .attr('y', (labelBarHeight * 0.4) / 2)
      .attr('x', function (d, i) {
        const textWidth = this.getAttribute('width');
        if (i < thirdMarker) {
          return 0;
        } else if (i >= thirdMarker && i < thirdMarker * 2) {
          return -(textWidth / 2);
        } else {
          return -textWidth;
        }
      })
      .attr('fill', 'white')
      .style('visibility', 'hidden')
      .attr('class', 'flyout');

    /**
     * Adding bars
     */
    const positive = chart.append('g');

    positive
      .selectAll('rect')
      .data(this.data.filter((d) => d.score > 0))
      .enter()
      .append('rect')
      .attr('fill', (d) => this.colorLabel(d.score))
      .attr('height', (d) => y(d.score))
      .attr('width', x.bandwidth())
      .attr('x', (i) => x(i.chartID))
      .attr('transform', (d) => 'translate(0, ' + (positiveHeight - y(d.score)) + ')');

    const negative = chart
      .append('g')
      .attr('transform', 'translate(0, ' + (positiveHeight + labelBarHeight) + ')');

    negative
      .selectAll('rect')
      .data(this.data.filter((d) => d.score < 0))
      .enter()
      .append('rect')
      .attr('fill', (d) => this.colorLabel(d.score))
      .attr('height', (d) => y(Math.abs(d.score)))
      .attr('width', x.bandwidth())
      .attr('x', (i) => x(i.chartID));

    /**
     * Setup dragging behaviour to select/deselect items in the chart
     */
    this.addDrag();
  }

  colorLabel(score) {
    if (score <= -0.75) {
      return this.veryNegativeColour;
    } else if (score > -0.75 && score <= -0.25) {
      return this.negativeColour;
    } else if (score > -0.25 && score <= 0.25) {
      return this.neutralColour;
    } else if (score > 0.25 && score <= 0.75) {
      return this.positiveColour;
    } else if (score > 0.75) {
      return this.veryPositiveColour;
    }
  }

  addDrag() {
    let drag = this.drag;

    this.el.addEventListener('mousedown', function (event) {
      if (event.button == 0) {
        drag = new Drag(event.x, event.y);
      }
    });

    const selected = this.selected;
    const labelRects = this.labelRects;

    window.addEventListener('mouseup', (event) => {
      if (!drag) {
        return;
      }

      drag.setEnd(event.x, event.y);
      if (drag.isDrag()) {
        labelRects.each(function (d) {
          if (drag.isInDrag(this)) {
            if (!selected.includes(d)) {
              selected.add(d);
            } else {
              selected.remove(d);
            }
          }
        });
      }
    });
  }

  addZoom() {
    window.addEventListener('dblclick', (e) => {
      if (this.width != this.fullWidth) {
        const clientX = e.clientX;
        const bounding = this.el.getBoundingClientRect();

        const translatedClick = clientX - bounding.x;
        const zoomPos = (translatedClick / bounding.width) * 100;

        this.width = this.fullWidth;
        this.empty();
        this.createChart();

        const zoomedBounding = this.el.getBoundingClientRect();
        const toScroll = (zoomedBounding.width / 100) * zoomPos - bounding.width / 2;

        this.el.parentNode.scrollLeft = toScroll;
      } else {
        this.width = this.boundingWidth;
        this.empty();
        this.createChart();
      }
    });
  }

  addChangeHandler(handler) {
    this.changeHandlers.push(handler);
  }

  empty() {
    const parent = d3.select(this.el);
    parent.selectAll('*').remove();
    parent.attr('width', null).attr('viewBox', null);
  }

  resetSelection() {
    this.selected.resetSelection();
  }
}

class Drag {
  constructor(x, y) {
    this.start = null;
    this.end = null;
    this.dragMarker = null;
    this.setStart(x, y);
  }

  setStart(x, y) {
    this.start = { x, y };
    this.dragMarker = document.createElement('div');
    const dragMarker = this.dragMarker;
    dragMarker.style.border = '1px dashed black';
    dragMarker.style.position = 'fixed';
    dragMarker.style.left = x + 'px';
    dragMarker.style.top = y + 'px';

    document.body.appendChild(dragMarker);

    function mouseMoveHandler(e) {
      const width = Math.abs(e.clientX - x);
      dragMarker.style.width = width + 'px';

      if (e.clientX - x < 0) {
        dragMarker.style.left = x - width + 'px';
      }

      const height = Math.abs(e.clientY - y);
      dragMarker.style.height = height + 'px';

      if (e.clientY - y < 0) {
        dragMarker.style.top = y - height + 'px';
      }
    }

    window.addEventListener('mousemove', mouseMoveHandler);
  }

  setEnd(x, y) {
    this.end = { x, y };
    this.dragMarker.remove();
  }

  isDrag() {
    return Math.abs(this.start.x - this.end.x) > 10 || Math.abs(this.start.y - this.end.y) > 10;
  }

  isInDrag(el) {
    // eslint-disable-next-line
    const { left, right, top, bottom } = el.getBoundingClientRect();

    const dragBounds = {
      left: this.start.x < this.end.x ? this.start.x : this.end.x,
      right: this.start.x > this.end.x ? this.start.x : this.end.x,
      top: this.start.y > this.end.y ? this.start.y : this.end.y,
      bottom: this.start.y < this.end.y ? this.start.y : this.end.y,
    };
    return right > dragBounds.left && left < dragBounds.right;
  }
}

class SelectionHandler {
  constructor(changeHandlers = [], selectedColour = null) {
    this.changeHandlers = changeHandlers;
    this.selectedColour = selectedColour;
    this.selected = [];
    this.indicators = new Map();
  }

  add(d) {
    this.selected.push(d);
    d3.select(this.indicators.get(d)).attr('fill', this.selectedColour);
    this.emitChange();
  }

  remove(d) {
    this.selected.splice(this.selected.indexOf(d), 1);
    d3.select(this.indicators.get(d)).attr('fill', 'transparent');
    this.emitChange();
  }

  includes(d) {
    return this.selected.includes(d);
  }

  addIndicator(d, el) {
    this.indicators.set(d, el);
  }

  emitChange() {
    this.changeHandlers.forEach((handler) => handler(this.selected));
  }

  resetSelection() {
    this.selected = [];
    this.indicators.forEach((indicator) => {
      d3.select(indicator).attr('fill', 'transparent');
    });
    this.emitChange();
  }
}
